import { startInstantSession } from '@cannect/services/resources/instantConsultation/requests'
import type { TSessionCallListItem } from '@cannect/services/resources/instantConsultation/types'
import { format, parseISO } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { formatReal } from 'utils/formatters'
import { PiStethoscopeLight } from 'react-icons/pi'

import * as Styles from '../CardQueries/styles'

type SessionCallCardProps = {
  session: TSessionCallListItem
}

function recipeHref(recipe: TSessionCallListItem['recipe']): string | null {
  if (!recipe) return null
  if (recipe.link_pdf) return recipe.link_pdf
  if (recipe.token) return `/receitas/${recipe.token}`
  return null
}

export function SessionCallCard({ session }: SessionCallCardProps) {
  const history = useHistory()
  const [loading, setLoading] = useState(false)

  const created = parseISO(session.createdAt)
  const dateLabel = format(created, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  const isOpen = !session.concluded_at
  const doctorName = session.prescriber?.professional_name ?? 'Médico a definir'
  const amountDisplay = session.amount ? formatReal(session.amount) : formatReal('99')
  const recipeUrl = recipeHref(session.recipe)

  let statusLabel = 'Concluída'
  if (isOpen) {
    if (session.status_payment === 'waiting_payment') {
      statusLabel = 'Aguardando pagamento'
    } else if (session.precriber_id) {
      statusLabel = 'Em atendimento'
    } else {
      statusLabel = 'Na fila / aguardando médico'
    }
  }

  const handleContinue = async () => {
    if (!session.uuid) {
      toast.error('Sessão inválida.')
      return
    }
    setLoading(true)
    try {
      const startResponse = await startInstantSession(session.uuid)
      if (!startResponse?.success && !startResponse?.already_in_queue) {
        toast.error(startResponse?.message || 'Não foi possível retomar a consulta agora.')
        return
      }
      history.push('/consulta-na-hora', { resumeSessionId: session.id })
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível retomar a consulta.'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Styles.Wrapper status="waiting_appointment">
      <Styles.CardContent>
        <Styles.InfoBox className="info">
          <Styles.IconBox>
            <PiStethoscopeLight size={24} />
          </Styles.IconBox>
          <div>
            <Styles.Name>{doctorName}</Styles.Name>
            <span
              style={{
                display: 'inline-block',
                marginTop: 4,
                fontSize: 11,
                fontWeight: 600,
                color: '#6B7B3C',
                textTransform: 'uppercase',
                letterSpacing: 0.5
              }}>
              Consulta na hora
            </span>
          </div>
        </Styles.InfoBox>

        <Styles.DateInfo>{dateLabel}</Styles.DateInfo>

        <Styles.AppointmentInfo>
          {amountDisplay} · {statusLabel}
        </Styles.AppointmentInfo>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {isOpen && (
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                handleContinue()
              }}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                background: '#6B7B3C',
                color: '#fff',
                fontWeight: 600,
                fontSize: 13,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando…
                </span>
              ) : (
                'Continuar consulta'
              )}
            </button>
          )}
          {!isOpen && recipeUrl && (
            <a
              href={recipeUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 13, fontWeight: 600, color: '#6B7B3C' }}>
              Ver receita
            </a>
          )}
        </div>
      </Styles.CardContent>
    </Styles.Wrapper>
  )
}
